import { useState } from "react";
import { useAuth } from "@/AuthContext";
import { Navigation, Eye, EyeOff, Loader2 } from "lucide-react";

function formatError(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const { login, register } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Load remembered email
  useState(() => {
    const saved = localStorage.getItem("decidefast_email");
    if (saved) { setEmail(saved); setRememberMe(true); }
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
      } else {
        if (!name.trim()) { setError("Name is required"); setLoading(false); return; }
        await register(email, password, name);
      }
      if (rememberMe) localStorage.setItem("decidefast_email", email);
      else localStorage.removeItem("decidefast_email");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0D1B2A] flex items-center justify-center p-6 relative">
      {/* Background */}
      <div className="fixed inset-0 bg-cover bg-center opacity-[0.06] pointer-events-none"
        style={{ backgroundImage: "url(https://static.prod-images.emergentagent.com/jobs/26a4f524-cd7c-42a9-b86d-e39aad365f48/images/0b1fa7d49a7483fea68a4b7508246ea399fd5589d2c5973459110f97b2902455.png)" }} />

      <div className="w-full max-w-md relative z-10">
        {/* Brand */}
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-3 mb-3">
            <Navigation className="w-8 h-8 text-[#C2440E]" />
            <h1 className="font-heading font-black text-4xl tracking-tighter text-white">DecideFast</h1>
          </div>
          <p className="font-mono text-[11px] text-white/90 tracking-[0.25em] uppercase">GPS for founders</p>
        </div>

        {/* Card */}
        <div className="bg-[#15283E] border border-white/[0.08] rounded-xl p-8" style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)" }}>
          {/* Tabs */}
          <div className="flex bg-[#0a1520] border border-white/[0.06] rounded-lg p-1 gap-1 mb-8">
            <button data-testid="auth-tab-login" onClick={() => { setIsLogin(true); setError(""); }}
              className={`flex-1 py-2.5 text-sm font-semibold rounded-md transition-all ${isLogin ? "bg-[#15283E] text-white border border-white/[0.1]" : "text-white/90 hover:text-white/70"}`}>
              Log In
            </button>
            <button data-testid="auth-tab-signup" onClick={() => { setIsLogin(false); setError(""); }}
              className={`flex-1 py-2.5 text-sm font-semibold rounded-md transition-all ${!isLogin ? "bg-[#15283E] text-white border border-white/[0.1]" : "text-white/90 hover:text-white/70"}`}>
              Sign Up
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name (signup only) */}
            {!isLogin && (
              <div>
                <label className="font-mono text-[10px] font-bold tracking-[0.2em] uppercase text-white/90 mb-2 block">Name</label>
                <input data-testid="auth-name-input" type="text" value={name} onChange={e => setName(e.target.value)}
                  placeholder="Your name"
                  className="w-full bg-[#0D1B2A] border border-white/[0.1] focus:border-[#C2440E]/50 focus:ring-1 focus:ring-[#C2440E]/30 transition-all px-4 py-3 rounded-lg text-[15px] text-white/90 placeholder:text-white/40 outline-none" />
              </div>
            )}

            {/* Email */}
            <div>
              <label className="font-mono text-[10px] font-bold tracking-[0.2em] uppercase text-white/90 mb-2 block">Email</label>
              <input data-testid="auth-email-input" type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com" autoComplete="email"
                className="w-full bg-[#0D1B2A] border border-white/[0.1] focus:border-[#C2440E]/50 focus:ring-1 focus:ring-[#C2440E]/30 transition-all px-4 py-3 rounded-lg text-[15px] text-white/90 placeholder:text-white/40 outline-none" />
            </div>

            {/* Password */}
            <div>
              <label className="font-mono text-[10px] font-bold tracking-[0.2em] uppercase text-white/90 mb-2 block">Password</label>
              <div className="relative">
                <input data-testid="auth-password-input" type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="Enter password" autoComplete={isLogin ? "current-password" : "new-password"}
                  className="w-full bg-[#0D1B2A] border border-white/[0.1] focus:border-[#C2440E]/50 focus:ring-1 focus:ring-[#C2440E]/30 transition-all px-4 py-3 pr-12 rounded-lg text-[15px] text-white/90 placeholder:text-white/40 outline-none" />
                <button type="button" data-testid="toggle-password-visibility" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/80 hover:text-white/60 transition-colors">
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Remember me */}
            <label className="flex items-center gap-2.5 cursor-pointer" data-testid="remember-me-checkbox">
              <input type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)}
                className="w-4 h-4 rounded border-white/20 bg-[#0D1B2A] text-[#C2440E] focus:ring-[#C2440E]/30 cursor-pointer" />
              <span className="text-sm text-white/85">Remember me</span>
            </label>

            {/* Error */}
            {error && <p data-testid="auth-error" className="text-orange-400 text-sm font-medium">{error}</p>}

            {/* Submit */}
            <button data-testid="auth-submit-button" type="submit" disabled={loading || !email || !password}
              className="w-full bg-[#C2440E] hover:bg-[#A6390C] text-white font-bold py-4 text-lg rounded-xl flex items-center justify-center gap-2.5 transition-all disabled:opacity-30 disabled:cursor-not-allowed active:scale-[0.97] shadow-lg shadow-[#C2440E]/20">
              {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> {isLogin ? "Logging in..." : "Creating account..."}</> :
                isLogin ? "Log In" : "Create Account"}
            </button>
          </form>
        </div>

        <p className="text-center text-[11px] text-white/80 mt-6">
          Your inputs are yours. We only learn how founders think — Not what they plan.
        </p>
      </div>
    </div>
  );
}
